import { useObserver } from 'mobx-react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { removeTypename } from 'shared/helpers/utils';
import { GetDutchiePosLoyaltyConfigDocument } from 'src/gql/graphql';
import type {
  DutchiePosLoyaltyConfig,
  GetDutchiePosLoyaltyConfigQuery,
  RewardsActivity,
  RewardsListV2,
  RewardV2,
} from 'src/gql/graphql';
import useUser from 'src/hooks/use-user';
import useUI from 'src/hooks/use-ui';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useKioskPro } from 'src/hooks/use-kiosk-pro';
import { useGetRewards } from 'src/components/loyalty/use-get-rewards';
import {
  RedemptionType,
  TierEarningBasis,
  formatLoyaltyExpirationDate,
} from 'src/components/loyalty/rewards/dpos-utils';

export type UseLoyaltyReturn = {
  loyaltyConfig: GetDutchiePosLoyaltyConfigQuery['getDutchiePosLoyaltyConfig'];
  loading: boolean;
  formatValueForDisplay: (value: number, label?: string, isLoyaltyPoints?: boolean) => string;
  formatValueForDaysForExpire: (daysToExpiration: number) => string;
  formatExpirationDate: (isoDate: string) => string;
  rewardsProgramName: string;
  balanceDaysToExpiration: number | null | undefined;
  badgeColor: string;
  currentTierName: string | undefined;
  currentTierThreshold: number | undefined;
  nextTierName: string | undefined;
  currentBalance: number;
  currentYearTierBalance: number;
  expirationDate: string | undefined;
  soonestExpiringBalance: number | null;
  soonestExpirationDate: string | null;
  targetBalance: number;
  targetRewardsBalance: number;
  progressPercentageToNextTier: string;
  progressPercentageToNextReward: string;
  progressToNextReward: number;
  currentRewardsBalance: number;
  isLoyaltyMember: boolean;
  isPoints: boolean;
  hasTiers: boolean;
  redemptionType: string;
  isSingleTier: boolean;
  isDiscountRedemption: boolean;
  posCustomerId: string | undefined;
  filteredRewardsActivity?: Array<RewardsActivity>;
  loyaltyEnterpriseConfigData?: DutchiePosLoyaltyConfig | undefined;
  rewardsEnterpriseData?: RewardsListV2 | undefined;
  loyaltyWidgetEnabled?: boolean;
  rewardsArray: Array<RewardV2>;
  showDiscountRewardsOnly: boolean;
  showRewardsBlockOnWeb: boolean;
  accrualRate: number | null;
};

export type UseLoyaltyOptions = {
  skip?: boolean;
};

export type ShowRewardsBlockOnWebInput = {
  isAlpineIQEnabled: boolean | undefined;
  isSpringbigEnabled: boolean | undefined;
  isShowNewLoyaltyConfigOptionsFlagEnabled: boolean;
  isDutchiePOSLoyaltyEnabled: boolean | undefined;
  loyaltyConfig: DutchiePosLoyaltyConfig | null | undefined;
};

/** Pure visibility rule for the checkout rewards block (Alpine/SpringBig vs DPOS + optional config). */
export function getShowRewardsBlockOnWeb({
  isAlpineIQEnabled,
  isSpringbigEnabled,
  isShowNewLoyaltyConfigOptionsFlagEnabled,
  isDutchiePOSLoyaltyEnabled,
  loyaltyConfig,
}: ShowRewardsBlockOnWebInput): boolean {
  return (
    !!isAlpineIQEnabled ||
    !!isSpringbigEnabled ||
    (isShowNewLoyaltyConfigOptionsFlagEnabled
      ? !!isDutchiePOSLoyaltyEnabled && loyaltyConfig != null && !!loyaltyConfig.onlineRedemptionEnabledOnWeb
      : !!isDutchiePOSLoyaltyEnabled)
  );
}

/** Shared skip + integration flags for Dutchie POS loyalty queries (config + rewards). */
function useLoyaltyQueryPrerequisites(options?: UseLoyaltyOptions) {
  const { isKiosk } = useUI();
  const isKioskProEnabled = useKioskPro();
  const User = useUser();
  const isUserLoading = useObserver(() => User.loading);
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const phone = useObserver(() => User.phone);
  const isUserLoadedAndLoggedIn = !isUserLoading && isLoggedIn;

  const { dispensary } = useDispensary();
  const isDutchiePOSLoyaltyEnabled = dispensary?.consumerDispensaryIntegrations?.dutchieposloyalty?.enabled;
  const isAlpineIQEnabled = dispensary?.consumerDispensaryIntegrations?.alpineiq?.enabled;
  const isSpringbigEnabled = dispensary?.consumerDispensaryIntegrations?.springbig?.enabled;

  const skip =
    options?.skip === true
      ? true
      : !isUserLoadedAndLoggedIn || !isDutchiePOSLoyaltyEnabled || !phone || (isKiosk && !isKioskProEnabled);

  return {
    skip,
    dispensary,
    isDutchiePOSLoyaltyEnabled,
    isAlpineIQEnabled,
    isSpringbigEnabled,
    phone,
    User,
  };
}

/**
 * Lightweight visibility for the checkout rewards section: same rules as `useLoyalty().showRewardsBlockOnWeb`
 * without `useGetRewards` or tier/reward calculations. Uses the same loyalty config query + Apollo cache as `useLoyalty`.
 */
export function useShowRewardsBlockOnWeb(): boolean {
  const flags = useFlags();
  const isShowNewLoyaltyConfigOptionsFlagEnabled =
    flags['pos.backoffice.show-new-loyalty-config-options.rollout'] ?? false;

  const {
    skip,
    dispensary,
    isAlpineIQEnabled,
    isSpringbigEnabled,
    isDutchiePOSLoyaltyEnabled,
  } = useLoyaltyQueryPrerequisites();

  const { data: loyaltyConfigData } = useQuery(GetDutchiePosLoyaltyConfigDocument, {
    variables: {
      dispensaryId: dispensary?.id,
    },
    skip,
  });

  const loyaltyConfig = loyaltyConfigData?.getDutchiePosLoyaltyConfig;

  return getShowRewardsBlockOnWeb({
    isAlpineIQEnabled,
    isSpringbigEnabled,
    isShowNewLoyaltyConfigOptionsFlagEnabled,
    isDutchiePOSLoyaltyEnabled,
    loyaltyConfig,
  });
}

export const useLoyalty = (
  activeEnterpriseId: string | undefined = undefined,
  loyaltyEnterpriseConfigData: DutchiePosLoyaltyConfig | undefined = undefined,
  rewardsEnterpriseData: RewardsListV2 | undefined = undefined,
  options?: UseLoyaltyOptions
): UseLoyaltyReturn => {
  const flags = useFlags();
  const isLoyaltyWidgetEnabled = flags['pos.ecomm.show-new-loyalty-widget.rollout'] ?? false;
  const isSkipSMSSendingEnabled = flags['pos.customers.skip-sms-sending.rollout'] ?? false;
  const isShowNewLoyaltyConfigOptionsFlagEnabled =
    flags['pos.backoffice.show-new-loyalty-config-options.rollout'] ?? false;

  const {
    skip,
    dispensary,
    isDutchiePOSLoyaltyEnabled,
    isAlpineIQEnabled,
    isSpringbigEnabled,
    phone,
    User,
  } = useLoyaltyQueryPrerequisites(options);
  const birthday = useObserver(() => User.birthday);

  const { data: loyaltyConfigData, loading: loyaltyConfigLoading } = useQuery(GetDutchiePosLoyaltyConfigDocument, {
    variables: {
      dispensaryId: dispensary?.id,
    },
    skip: !!loyaltyEnterpriseConfigData || skip,
  });

  const { data: rewardsData, loading: rewardsLoading } = useGetRewards({
    dispensaryId: dispensary?.id,
    birthday,
    phone,
    pin: isSkipSMSSendingEnabled ? 'skip' : '',
    customerId: User?.id,
    skip: !!rewardsEnterpriseData || skip,
  });

  const loyaltyConfig = activeEnterpriseId
    ? loyaltyEnterpriseConfigData
    : loyaltyConfigData?.getDutchiePosLoyaltyConfig;
  const rewards = activeEnterpriseId ? rewardsEnterpriseData : rewardsData?.getRewardsV2;
  const rewardsArray = rewards?.rewards.filter((reward) => reward?.isRedeemableOnline);
  const rewardsActivity = rewards?.rewardsActivity;
  const isLoyaltyMember = rewards?.isLoyaltyMember ?? false;
  const isPoints = loyaltyConfig?.tierEarningBasis === TierEarningBasis.points;
  const isDiscountRedemption = loyaltyConfig?.redemptionType?.toLowerCase() === RedemptionType.discount;
  const filteredRewardsActivity = rewardsActivity
    ?.filter((rewardActivity) => rewardActivity !== null)
    .map((rewardData) => removeTypename(rewardData));

  // Tiers calculations for single tier or multiple tiers (coerce for number/string from API)
  const rewardsTierId = rewards?.tierId != null ? Number(rewards.tierId) : undefined;
  const currentTierIndex =
    loyaltyConfig?.tiers?.findIndex((tier) => tier?.id != null && Number(tier.id) === rewardsTierId) ?? -1;
  const hasMatchingTier = currentTierIndex >= 0;
  const currentTier = hasMatchingTier ? loyaltyConfig?.tiers?.[currentTierIndex] : undefined;
  const nextTier = hasMatchingTier ? loyaltyConfig?.tiers?.[currentTierIndex + 1] : loyaltyConfig?.tiers?.[0];
  const { onlineName } = loyaltyConfig ?? {};
  const { badgeColor, threshold: currentTierThreshold = 0, name: currentTierName } = currentTier ?? {};
  const { name: nextTierName, threshold: nextTierThreshold } = nextTier ?? {};

  const activeTierAccrualRate = currentTier?.accrualRate ?? null;
  const envelopeAccrualRate = loyaltyConfig?.accrualRate ?? null;
  const accrualRate = activeTierAccrualRate ?? envelopeAccrualRate;

  const currentBalance = Number(rewards?.balance ?? 0);
  const currentYearTierBalance = Number(rewards?.currentYearTierBalance ?? 0);
  const expirationDate = rewards?.expirationDate;
  const soonestExpiringBalance = rewards?.soonestExpiringBalance ?? null;
  const soonestExpirationDate = rewards?.soonestExpirationDate ?? null;

  // When user has not met any tier yet, progress is from 0 to first tier threshold
  const effectiveCurrentTierThreshold = hasMatchingTier ? currentTierThreshold : 0;

  // Handle single tier vs multiple tiers logic
  const isSingleTier = loyaltyConfig?.tiers?.length === 1;
  const singleTierThreshold = loyaltyConfig?.tiers?.[0]?.threshold ?? 0;
  const targetBalance = isSingleTier ? singleTierThreshold : nextTierThreshold ?? 0;
  const progressToNextTier = isSingleTier ? singleTierThreshold : targetBalance - effectiveCurrentTierThreshold;
  const currentProgress = isSingleTier
    ? currentYearTierBalance
    : currentYearTierBalance - effectiveCurrentTierThreshold;
  const progressPercentageToNextTier =
    progressToNextTier === 0 ? '0%' : `${Math.min((currentProgress / progressToNextTier) * 100, 100)}%`;

  // Rewards calculations
  const rewardslist =
    rewards?.rewards.filter((reward) => reward?.available).sort((a, b) => (a?.costV2 ?? 0) - (b?.costV2 ?? 0)) ?? [];
  const nextReward = rewardslist.find((reward) => currentBalance < (reward?.costV2 ?? 0));
  const targetRewardsBalance = nextReward?.value ? Number(nextReward.costV2) : 0;
  const progressToNextReward = targetRewardsBalance - currentBalance;
  // Progress toward next reward = current balance vs next reward cost (points/dollars needed)
  const progressPercentageToNextReward =
    progressToNextReward === 0 || targetRewardsBalance === 0
      ? '0%'
      : `${Math.min((currentBalance / targetRewardsBalance) * 100, 100)}%`;

  const balanceDaysToExpiration = expirationDate
    ? Math.ceil((new Date(expirationDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const formatValueForDisplay = (value: number, label: string, isLoyaltyPoints: boolean): string => {
    if (isLoyaltyPoints) {
      return `${String(parseFloat(value.toFixed(2)))} ${label}`;
    }

    return currencyFormatter.format(value);
  };

  const formatValueForDaysForExpire = (daysToExpiration: number): string => {
    if (!expirationDate) {
      return isDiscountRedemption ? 'Rewards points do not expire' : 'Rewards do not expire';
    }
    const formattedDate = formatLoyaltyExpirationDate(expirationDate);
    const formattedBalance = formatValueForDisplay(
      currentBalance,
      isDiscountRedemption ? 'pts' : 'cash',
      isDiscountRedemption
    );
    if (daysToExpiration > 0) {
      const exp = new Date(expirationDate);
      const now = new Date();
      const expiresUTCToday =
        exp.getUTCFullYear() === now.getUTCFullYear() &&
        exp.getUTCMonth() === now.getUTCMonth() &&
        exp.getUTCDate() === now.getUTCDate();
      return expiresUTCToday ? `${formattedBalance} expires today` : `${formattedBalance} expires ${formattedDate}`;
    }
    return `${formattedBalance} expired ${formattedDate}`;
  };

  const shouldShowLoyaltyWidget =
    isLoyaltyWidgetEnabled &&
    loyaltyConfig?.isEnabled &&
    (isShowNewLoyaltyConfigOptionsFlagEnabled ? loyaltyConfig?.loyaltyWidgetEnabledOnWeb ?? true : true) &&
    ((isLoyaltyMember && loyaltyConfig.loyaltyRequireOptIn) || !loyaltyConfig.loyaltyRequireOptIn);

  // viewDiscountRewards is view only when true
  const showDiscountRewardsOnly =
    isShowNewLoyaltyConfigOptionsFlagEnabled && isDutchiePOSLoyaltyEnabled
      ? !!loyaltyConfig?.viewDiscountRewards
      : false;

  // Alpine/SpringBig always allow the checkout rewards block. Otherwise: new-options flag off →
  // mirror DPOS integration only; flag on → DPOS requires loaded config and onlineRedemptionEnabledOnWeb.
  const showRewardsBlockOnWeb = getShowRewardsBlockOnWeb({
    isAlpineIQEnabled,
    isSpringbigEnabled,
    isShowNewLoyaltyConfigOptionsFlagEnabled,
    isDutchiePOSLoyaltyEnabled,
    loyaltyConfig,
  });

  return {
    loyaltyConfig,
    loading: loyaltyConfigLoading || rewardsLoading,
    formatValueForDisplay,
    formatExpirationDate: formatLoyaltyExpirationDate,
    rewardsProgramName: onlineName ?? 'Rewards',
    balanceDaysToExpiration: balanceDaysToExpiration && balanceDaysToExpiration < 0 ? 0 : balanceDaysToExpiration,
    badgeColor: badgeColor ?? '#ffffff',
    currentTierName,
    currentTierThreshold,
    nextTierName,
    currentBalance,
    currentYearTierBalance,
    targetBalance,
    progressPercentageToNextTier,
    progressPercentageToNextReward,
    progressToNextReward,
    targetRewardsBalance,
    currentRewardsBalance: currentBalance,
    isLoyaltyMember,
    isPoints,
    redemptionType: loyaltyConfig?.redemptionType ?? '',
    hasTiers: (loyaltyConfig?.tiers?.length ?? 0) > 0,
    isSingleTier,
    isDiscountRedemption,
    posCustomerId: rewards?.posCustomerId ?? '',
    filteredRewardsActivity,
    formatValueForDaysForExpire,
    expirationDate: expirationDate ?? '',
    soonestExpiringBalance,
    soonestExpirationDate,
    loyaltyWidgetEnabled: shouldShowLoyaltyWidget ?? false,
    rewardsArray: (rewardsArray as Array<RewardV2>) ?? ([] as Array<RewardV2>),
    showDiscountRewardsOnly,
    showRewardsBlockOnWeb,
    accrualRate,
  };
};
